/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ISwatchItem, ProductComponentSwatchComponent, RatingComponent } from '@msdyn365-commerce/components';
import {
    getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    ProductDimension,
    ProductPrice,
    ProductsDataActions,
    ProductSearchResult
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useCallback, useEffect,  useState } from 'react';
import PriceComponent from './price.component';
// import { ProductsDataActions } from '@msdyn365-commerce/retail-proxy';
export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    retiredProductsLabel?: string;
    retailerExclusiveLabel?: string;
    NZRetiredProductsLabel?: string;
    NZRetailerExclusiveLabel?: string;
    retiredProductStockLabel?:string
    retailerExclusiveStockLabel?:string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    productIds?: number[];
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <div className='msc-product__unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
}

/**
 * Gets the react node for product availability.gh
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(
    inventoryAvailabilityLabel: string | undefined,
    productAttributeRetiredProducts: boolean | undefined,
    productAttributeRetailExclusive: boolean | undefined,
    retailerExclusiveStockLabel?:string,
    retiredProductStockLabel?:String,
    
): JSX.Element | null {
    if (!productAttributeRetailExclusive && !productAttributeRetiredProducts) {
        if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
            return null;
        }
        const oosClass = inventoryAvailabilityLabel.toLowerCase() === 'out of stock' ? 'oos' : '';
        return (
            <div className={`msc-product__availability ${oosClass}`}>
                <span>{inventoryAvailabilityLabel}</span>
            </div>
        );
    } else {
        const oosClass = inventoryAvailabilityLabel?.toLowerCase() === 'out of stock' ? '' : '';
        let inventoryAvailability;
        if (productAttributeRetailExclusive) {
         
            inventoryAvailability=retailerExclusiveStockLabel??"Exclusive";
        }
        if (productAttributeRetiredProducts) {
         
            inventoryAvailability=retiredProductStockLabel??"Retired";
        }
        return (
            <div className={`msc-product__availability ${oosClass}`}>
                <span>{inventoryAvailability ?? ''}</span>
            </div>
        );
    }
    //@ts-ignore
    return null;
}

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, {
        product,
        catalogId
    });
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    retailerExclusiveLabel,
    NZRetailerExclusiveLabel,
    retiredProductStockLabel,
    retailerExclusiveStockLabel,
    retiredProductsLabel,
    NZRetiredProductsLabel,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel,
    productIds
}) => {
    const product = data.product;
    // @ts-ignore
    const _getActiveProductAsync = async (product?: ProductSearchResult): Promise<ProductPrice | undefined> => {
        const staticArray: number[] = [product?.RecordId ?? 0];
        const catalogId = getCatalogId(context.request);

        try {
            const activePrice = await ProductsDataActions.getActivePricesAsync(
                { callerContext: context.actionContext, bypassCache: 'get' },
                {
                    ChannelId: context.request.apiSettings.channelId,
                    CatalogId: catalogId
                },
                staticArray ?? [],
                new Date(),
                null,
                null,
                true
            );
            return ArrayExtensions.firstOrDefault(activePrice);
        } catch (error) {
            console.error('Error fetching catalog product attributes:', error);
            return undefined;
        }
    };
 
    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    // @ts-ignore
    const [productAttributeBrandName, setProductAttributesBrandName] = useState<string | undefined>();
    const [productAttributeRetailExclusive, setProductAttributesRetailExclusive] = useState<boolean | undefined>(false);
    const [productAttributeRetiredProducts, setProductAttributesRetiredProducts] = useState<boolean | undefined>(false);
    // @ts-ignore
    const [activeProductPrice, setActiveProductPrice] = useState<ProductPrice | undefined>();
    const [processedProductId, setProcessedProductId] = useState(0);
    // Ref to track if effect has run
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
        const retiredProductsLabelString = retiredProductsLabel ?? 'Retired Products (D2C)';
        const retailerExclusiveLabelString = retailerExclusiveLabel ?? 'Retailer Exclusive (D2C)';

        const NZRetiredProductsLabelString = NZRetiredProductsLabel ?? 'BNZ Retired Products (D2C)';
        const NZRetailerExclusiveLabelString = NZRetailerExclusiveLabel ?? 'BNZ Retailer Exclusive (D2C)';

        const searchProductAttr = ['Brands'];
        const searchProductAttrs = [retailerExclusiveLabelString, retiredProductsLabelString,NZRetiredProductsLabelString,NZRetailerExclusiveLabelString];
    
        const productAttributesValue = product?.AttributeValues?.filter(
          value => value.Name !== null && value.Name !== undefined && searchProductAttr.some(attr => value.Name?.includes(attr))
        ).map(value => value.TextValue);
    
        const combinedAttributeValues = productAttributesValue?.join(', ');
        const productAttributes = product?.AttributeValues?.filter(
          value =>
            value.Name !== null &&
            value.Name !== undefined &&
            searchProductAttrs.some(label => value.Name?.includes(label) && value.BooleanValue === true)
        ).map(value => value.Name);
    
        // const retailerExclusive = productAttributes?.some(name => name?.toLowerCase() === retailerExclusiveLabelString?.toLowerCase());
        // const retiredProducts = productAttributes?.some(name => name?.toLowerCase() === retiredProductsLabelString?.toLowerCase());

        const retailerExclusive = productAttributes?.some((name: string | undefined) => 
            name?.toLowerCase() === retailerExclusiveLabelString?.toLowerCase() ||  name?.toLowerCase() === NZRetailerExclusiveLabelString?.toLowerCase() 
          );
          
          const retiredProducts = productAttributes?.some((name: string | undefined) => 
            name?.toLowerCase() === retiredProductsLabelString?.toLowerCase() ||  name?.toLowerCase() === NZRetailerExclusiveLabelString?.toLowerCase() 
          );
        setProductAttributesBrandName(combinedAttributeValues);
        setProductAttributesRetailExclusive(retailerExclusive);
        setProductAttributesRetiredProducts(retiredProducts);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);

    const fetchProductAttributes = useCallback(async () => {
        const retiredProductsLabelString = retiredProductsLabel ?? 'Retired Products (D2C)';
        const retailerExclusiveLabelString = retailerExclusiveLabel ?? 'Retailer Exclusive (D2C)';

        const NZRetiredProductsLabelString = NZRetiredProductsLabel ?? 'BNZ Retired Products (D2C)';
        const NZRetailerExclusiveLabelString = NZRetailerExclusiveLabel ?? 'BNZ Retailer Exclusive (D2C)';


        const searchProductAttrs = [retailerExclusiveLabelString, retiredProductsLabelString,NZRetiredProductsLabelString,NZRetailerExclusiveLabelString];
    
       
        const productAttributes = product?.AttributeValues?.filter(
          value =>
            value.Name !== null &&
            value.Name !== undefined &&
            searchProductAttrs.some(label => value.Name?.includes(label) && value.BooleanValue === true)
        ).map(value => value.Name);
    
        // const retailerExclusive = productAttributes?.some(name => name?.toLowerCase() === retailerExclusiveLabelString?.toLowerCase());
        // const retiredProducts = productAttributes?.some(name => name?.toLowerCase() === retiredProductsLabelString?.toLowerCase());


        const retailerExclusive = productAttributes?.some((name: string | undefined) => 
            name?.toLowerCase() === retailerExclusiveLabelString?.toLowerCase() || name?.toLowerCase() === NZRetailerExclusiveLabelString?.toLowerCase()
          );
          
          const retiredProducts = productAttributes?.some((name: string | undefined) => 
            name?.toLowerCase() === retiredProductsLabelString?.toLowerCase() || name?.toLowerCase() === NZRetiredProductsLabelString?.toLowerCase()
          );
        //4161 user story do not load price for retailer and retired 
        const activePrice = !retailerExclusive && !retiredProducts && await _getActiveProductAsync(product);
        if(activePrice){
            setActiveProductPrice(activePrice);
        }
        
      
      }, [product]);
     //@ts-ignore
      useEffect(() => {
        if (product?.RecordId && product?.RecordId !== processedProductId) {
          const timer = setTimeout(() => {
            fetchProductAttributes();
            setProcessedProductId(product?.RecordId);
          }, 3000);
    
          return () => clearTimeout(timer);
        }
      }, [product?.RecordId, fetchProductAttributes, processedProductId]);
    
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, {
            selectedProductId: item,
            selectedDimensions
        });
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';
    const retiredProductsLabelString = retiredProductsLabel ?? 'Retired Products (D2C)';
    const retailerExclusiveLabelString = retailerExclusiveLabel ?? 'Retailer Exclusive (D2C)';

    const NZRetiredProductsLabelString = NZRetiredProductsLabel ?? 'BNZ Retired Products (D2C)';
    const NZRetailerExclusiveLabelString = NZRetailerExclusiveLabel ?? 'BNZ Retailer Exclusive (D2C)';

    const searchProductAttr = ['Brands'];
    const searchProductAttrs = [retailerExclusiveLabelString, retiredProductsLabelString , NZRetiredProductsLabelString ,NZRetailerExclusiveLabelString];

    const productAttributesValue = product?.AttributeValues?.filter(
      value => value.Name !== null && value.Name !== undefined && searchProductAttr.some(attr => value.Name?.includes(attr))
    ).map(value => value.TextValue);

    const brandName = productAttributesValue?.join(', ');

    const productAttributes = product?.AttributeValues?.filter(
      value =>
        value.Name !== null &&
        value.Name !== undefined &&
        searchProductAttrs.some(label => value.Name?.includes(label) && value.BooleanValue === true)
    ).map(value => value.Name);

    // const retailerExclusive = productAttributes?.some(name => name?.toLowerCase() === retailerExclusiveLabelString?.toLowerCase());
    // const retiredProducts = productAttributes?.some(name => name?.toLowerCase() === retiredProductsLabelString?.toLowerCase());

    const retailerExclusive = productAttributes?.some((name: string | undefined) => 
        name?.toLowerCase() === retailerExclusiveLabelString?.toLowerCase() ||  name?.toLowerCase() === NZRetailerExclusiveLabelString?.toLowerCase()
      );
      
      const retiredProducts = productAttributes?.some((name: string | undefined) => 
        name?.toLowerCase() === retiredProductsLabelString?.toLowerCase()  ||  name?.toLowerCase() === NZRetiredProductsLabelString?.toLowerCase() 
      );
    return (
        <>
            {isEnabledProductDescription ? (
                <a
                    href={productPageUrl}
                    onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                    aria-label={renderLabel(
                        product.Name,
                        context.cultureFormatter.formatCurrency(product.Price),
                        product.AverageRating,
                        ratingAriaLabel,
                        product.TotalRatings,
                        ratingCountAriaLabel
                    )}
                    className='msc-product'
                    {...attribute}
                >
                    <div className='msc-product__image__description'>
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__title_description'>
                            <h5 className='msc-product__title__text'>{product.Name}</h5>
                            {!productAttributeRetiredProducts &&
                                !productAttributeRetailExclusive &&
                                renderPrice(
                                    context,
                                    typeName,
                                    id,
                                    product.Price,
                                    product.BasePrice,
                                    savingsText,
                                    freePriceText,
                                    originalPriceText,
                                    currentPriceText,
                                    isPriceMinMaxEnabled,
                                    priceResources,
                                    activeProductPrice
                                )}
                            {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                            {renderDescription(product.Description)}
                        </div>
                    </div>
                </a>
            ) : (
                <>
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={renderLabel(
                            product.Name,
                            context.cultureFormatter.formatCurrency(product.Price),
                            product.AverageRating,
                            ratingAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    >
                        <span className='msc-product__brand-name'>{brandName}</span>
                        <h5 className='msc-product__title'>{product.Name}</h5>
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                    </a>
                    <div className='msc-product__details'>
                        <a
                            href={productPageUrl}
                            onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                            aria-label={renderLabel(
                                product.Name,
                                context.cultureFormatter.formatCurrency(product.Price),
                                product.AverageRating,
                                ratingAriaLabel
                            )}
                            className='msc-product'
                            {...attribute}
                        >
                            <div className='msc-product__details_top'>
                                {renderPrice(
                                    context,
                                    typeName,
                                    id,
                                    product.Price,
                                    product.BasePrice,
                                    savingsText,
                                    freePriceText,
                                    originalPriceText,
                                    currentPriceText,
                                    undefined,
                                    undefined,
                                    activeProductPrice,
                                    productAttributeRetiredProducts,
                                    productAttributeRetailExclusive
                                )}
                                <div className={'msc-product__details_top_price'}>
                                    {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)}
                                </div>
                                {!context.app.config.hideRating &&
                                    renderRating(
                                        context,
                                        typeName,
                                        id,
                                        product.AverageRating,
                                        product.TotalRatings,
                                        ratingAriaLabel,
                                        ratingCountAriaLabel
                                    )}
                            </div>
                            <div className='msc-product__details_middle'>
                                {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
                                {renderProductDimensions(product.AttributeValues)}
                            </div>
                        </a>

                        <div className='msc-product__details_bottom'>
                            {renderProductAvailability(inventoryLabel, retiredProducts, retailerExclusive,retailerExclusiveStockLabel ,retiredProductStockLabel) ? (
                                <div className='msc-product__availability'>
                                    {renderProductAvailability(
                                        inventoryLabel,
                                        retiredProducts,
                                        retailerExclusive,
                                        retailerExclusiveStockLabel,
                                        retiredProductStockLabel
                                    )}
                                    {productComparisonButton &&
                                        renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
                                </div>
                            ) : (
                                <div className='msc-product__availability__center'>
                                    {productComparisonButton &&
                                        renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    basePrice?: number,
    adjustedPrice?: number,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    activeProductPrice?: ProductPrice,
    productAttributeRetiredProducts?: boolean,
    productAttributeRetailExclusive?: boolean
): JSX.Element | null {
    if (!productAttributeRetiredProducts && !productAttributeRetailExclusive) {
        const price: ProductPrice = {
            BasePrice: activeProductPrice?.BasePrice,
            AdjustedPrice: activeProductPrice?.AdjustedPrice,
            CustomerContextualPrice: activeProductPrice?.CustomerContextualPrice,
            TradeAgreementPrice: activeProductPrice?.TradeAgreementPrice,
            MaxVariantPrice: activeProductPrice?.MaxVariantPrice,
            MinVariantPrice: activeProductPrice?.MinVariantPrice,
            DiscountAmount: activeProductPrice?.DiscountAmount,
            CurrencyCode: activeProductPrice?.CurrencyCode,
            DiscountLines: activeProductPrice?.DiscountLines,
            SalesAgreementPrice: activeProductPrice?.SalesAgreementPrice,
            PriceSourceTypeValue: activeProductPrice?.PriceSourceTypeValue
        };
        return (
            <PriceComponent
                context={context}
                id={id}
                typeName={typeName}
                data={{ price: price }}
                savingsText={savingsText}
                freePriceText={freePriceText}
                originalPriceText={originalPriceText}
                isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                priceResources={priceResources}
            />
        );
    }
    return (
        <span className={'msc-price'}>
            <span className={'msc-price__actual'} dangerouslySetInnerHTML={{ __html: '&nbsp;' }}></span>
        </span>
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
